import { supabase } from '@/lib/supabase';
import type { PreparedImage } from './imagePrep';

const BUCKET = 'prescriptions';

export interface UploadedAttachment {
  storage_path: string;
  file_type: string;
  file_name: string;
}

/**
 * Upload an already-prepared image to
 * `prescriptions/{profile_id}/{event_id}/{name}` and return the full
 * storage path (including the bucket prefix) so edge functions can
 * resolve it without guessing.
 */
export async function uploadPrescriptionImage(
  profileId: string,
  eventId: string,
  image: PreparedImage,
): Promise<UploadedAttachment> {
  const objectPath = `${profileId}/${eventId}/${image.fileName}`;
  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(objectPath, image.blob, {
      contentType: image.fileType,
      upsert: false,
    });
  if (error) throw error;
  return {
    storage_path: `${BUCKET}/${objectPath}`,
    file_type: image.fileType,
    file_name: image.fileName,
  };
}

/** Signed URL for a private prescriptions object. Default: 10 minutes. */
export async function signedPrescriptionUrl(
  storagePath: string,
  expiresInSeconds = 60 * 10,
): Promise<string> {
  const objectPath = storagePath.startsWith(`${BUCKET}/`)
    ? storagePath.slice(BUCKET.length + 1)
    : storagePath;
  const { data, error } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(objectPath, expiresInSeconds);
  if (error || !data?.signedUrl) {
    throw new Error(error?.message ?? 'no signed url');
  }
  return data.signedUrl;
}
